import { Circle, Image, StyleSheet, Svg, Text, View } from '@react-pdf/renderer';
import React from 'react';
import { withTranslation } from 'react-i18next';
import commonStyles from 'views/SpecificationDocument/DocumentView/Common/commonStyles';
import Point from './Point';
import PointDrawing from './PointDrawing';
// @react-pdf/renderer's Text prop types don't account for the SVG-flavored usage below
// (fontSize as a direct prop rather than via `style`), even though it's valid at runtime.
const SvgText = Text;
// The interactive comment editor (layout/molecules/Playground/Playground.tsx) stores
// x/y/noteX/noteY as raw pixels on a fixed 750x650 canvas. All point coordinates must
// be scaled from that canvas into this document's image box.
const EDITOR_WIDTH = 750;
const EDITOR_HEIGHT = 650;
const COLUMN_GAP = 20;
const IMAGE_PADDING_RATIO = 35 / 525;
const IMAGE_WIDTH = 547;
const IMAGE_HEIGHT = 474;
const MARKER_RADIUS = 7;
const MARKER_BORDER_WIDTH = 1.5;
const MARKER_FONT_SIZE = 8;
// Two-digit numbers need a smaller font to fit inside the marker.
const MARKER_FONT_SIZE_TWO_DIGITS = 7;
// Shifts the text baseline down (as a fraction of font size) to center digits vertically.
const MARKER_TEXT_OFFSET_RATIO = 0.36;
const styles = StyleSheet.create({
    projectionContainer: {
        marginHorizontal: commonStyles.marginHorizontal,
    },
    projectionTitleWrapper: Object.assign({ flexDirection: 'row' }, commonStyles.fontM),
    projectionTitle: {
        fontWeight: 'bold',
    },
    titleImageGroup: {
        alignItems: 'flex-start',
    },
    imageWrapper: {
        position: 'relative',
    },
    bubbleColumn: {
        flexDirection: 'column',
        marginHorizontal: commonStyles.marginHorizontal,
    },
    firstBubbleColumn: {
        marginTop: COLUMN_GAP,
    },
    svgOverlay: {
        position: 'absolute',
        left: 0,
        top: 0,
    },
});
function parsePoints(points, type) {
    return points.filter((point) => point.typeCode === type);
}
function scalePoint(point, imageWidth, imageHeight) {
    return {
        x: (point.x * imageWidth) / EDITOR_WIDTH,
        y: (point.y * imageHeight) / EDITOR_HEIGHT,
    };
}
function Projection({ projection, type, t }) {
    const points = parsePoints(projection.points, type).sort((a, b) => a.y - b.y);
    const hasPoints = points.length > 0;
    const imageWidth = IMAGE_WIDTH;
    const imageHeight = IMAGE_HEIGHT;
    const imagePadding = imageWidth * IMAGE_PADDING_RATIO;
    return (React.createElement(React.Fragment, null,
        React.createElement(View, { style: styles.projectionContainer },
            React.createElement(View, { style: styles.titleImageGroup, wrap: false },
                React.createElement(View, { style: styles.projectionTitleWrapper },
                    React.createElement(Text, null,
                        t('projection'),
                        ":"),
                    React.createElement(Text, { style: styles.projectionTitle }, `${projection.name} ${t(type === 'DESIGNER'
                        ? 'designer_comments_with_addon'
                        : 'technologist_comments_with_addon')}
          `)),
                React.createElement(View, { style: [styles.imageWrapper, { width: imageWidth, height: imageHeight }] },
                    React.createElement(Image, { style: {
                            padding: imagePadding,
                            width: imageWidth,
                            height: imageHeight,
                            objectFit: 'contain',
                        }, src: projection.picture }),
                    hasPoints && (React.createElement(Svg, { style: [styles.svgOverlay, { width: imageWidth, height: imageHeight }], viewBox: `0 0 ${imageWidth} ${imageHeight}`, preserveAspectRatio: "none" }, points.map((point, orderIndex) => {
                        const pin = scalePoint(point, imageWidth, imageHeight);
                        const markerNumber = orderIndex + 1;
                        const markerFontSize = markerNumber > 9 ? MARKER_FONT_SIZE_TWO_DIGITS : MARKER_FONT_SIZE;
                        return (React.createElement(React.Fragment, { key: point.id },
                            React.createElement(Circle, { cx: pin.x, cy: pin.y, r: MARKER_RADIUS, fill: "#000", stroke: "#fff", strokeWidth: MARKER_BORDER_WIDTH }),
                            React.createElement(SvgText, { x: pin.x, y: pin.y + markerFontSize * MARKER_TEXT_OFFSET_RATIO, fill: "#fff", fontSize: markerFontSize, textAnchor: "middle", 
                                // SVG text ignores fontFamily/fontWeight passed as props; only `style` applies.
                                style: { fontFamily: 'Inter', fontWeight: 'bold' } }, markerNumber)));
                    })))))),
        hasPoints &&
            points.map((point, orderIndex) => (React.createElement(React.Fragment, { key: point.id },
                React.createElement(View, { style: [
                        styles.bubbleColumn,
                        { width: imageWidth },
                        orderIndex === 0 && styles.firstBubbleColumn,
                    ] },
                    React.createElement(Point, { point: point, orderIndex: orderIndex, projectionIdx: projection.number, width: imageWidth })),
                point.photo && (React.createElement(PointDrawing, { point: point, pointIndex: orderIndex, projection: projection })))))));
}
export default withTranslation('specification')(Projection);
